import React, { createContext } from "react";

import { Store } from "@apps/mf-overview/src/ts/stores/Store";

/**
 * Creates a React context (ie. a Provider / Consumer pair), which allows us to
 * propagate references to the Store all the way down through our render tree.
 */

// @ts-ignore
// tslint:disable-next-line variable-name
const StoreContext = createContext<Store>();

// Provide a meaningful displayName for development environments
StoreContext.displayName = "StoreContext";


/**
 * Shorthand typing for omitting certain keys from an object.
 */
type Omit<T, K> = Pick<T, Exclude<keyof T, K>>;

/**
 * The inject method is just a bit of syntaxtic sugar for injecting data from
 * the store into components.
 *
 * It allows definition of a callback, which will recieve a reference to the
 * store defined in the provider, along with any props given when the component
 * is mounted into the DOM.
 */
export function injectStore<T, P extends Record<string, unknown>>(
    callback: (store: Store) => T,
    WrappedComponent: React.ComponentType<P>, // tslint:disable-line variable-name
): React.FC<Omit<P, keyof T>> {
    // In order for hot reloading to work as extected, we're going to have to
    // create a wrapper around the actual rendering
    // tslint:disable-next-line variable-name
    const HotReloader: React.FC<P> = (props) => <WrappedComponent {...props} />;

    // Wrap the renderer in our consumer, so store will be propagated to it
    // tslint:disable-next-line variable-name
    const InjectStore: React.FC<Omit<P, keyof T>> & { WrappedComponent: React.ComponentType<P> } = (props) => (
        <StoreContext.Consumer>
            {
                // There's a bug in TypeScript v3.2.1 - for some reason it will
                // report that `Omit<P, keyof T> & T` is not assignable to to P,
                // even though it really should be!
                // @ts-ignore
                store => <HotReloader {...props} {...callback(store)} />
            }
        </StoreContext.Consumer>
    );

    // Apply a meaningful display name for easier debugging in React devtools
    InjectStore.displayName = "InjectStore";
    HotReloader.displayName = "InjectStore(HotReloader)";

    // Expose wrapped component context globally
    InjectStore.WrappedComponent = WrappedComponent;

    // Return the injector publicly
    return InjectStore;
}

/**
 * Expose a reference to the StoreProvider publicly, so that the Root component
 * can mount it and thereby provide the reference globally within it's render
 * tree.
 */
// tslint:disable-next-line variable-name
export const StoreProvider = StoreContext.Provider;
